/**
 * Local sample league data (fixtures mode).
 *
 * Standings, the live head-to-head matchup, the weekly scoreboard and the
 * franchise trophies are all *derived* deterministically from the league's
 * teams + scoring type + history (see utils/standingsUtils.js and
 * LeagueLayout's currentMatchup). So a fully-populated sample league here makes
 * every one of those surfaces render realistically without the API.
 *
 * enrichLeagueWithFixtures() overlays a 10-team head-to-head league (one team
 * owned by the signed-in user) plus a few seasons of history onto whatever the
 * league loader returned, keeping the league's own id / name / season.
 */

// 10 franchises. Team 1 is claimed by the signed-in user at enrich time.
const SAMPLE_TEAMS = [
  { name: 'Turner Tomahawks', ownerName: 'You', logoEmoji: '🐻', acronym: 'TT' },
  { name: 'Double Down', ownerName: 'Ian Duffy', logoEmoji: '🎲', acronym: 'DBL' },
  { name: 'Cascade Kings', ownerName: 'Elena Petrova', logoEmoji: '👑', acronym: 'CK' },
  { name: 'Motor City Maulers', ownerName: 'Dwayne Foster', logoEmoji: '🔧', acronym: 'MM' },
  { name: 'Harbor Hounds', ownerName: 'Priya Nair', logoEmoji: '🐕', acronym: 'HH' },
  { name: 'Summit Serpents', ownerName: 'Kenji Watanabe', logoEmoji: '🐍', acronym: 'SS' },
  { name: 'Delta Dynamos', ownerName: 'Sofia Alvarez', logoEmoji: '⚡', acronym: 'DD' },
  { name: 'Ironclad Owls', ownerName: "Liam O'Connor", logoEmoji: '🦉', acronym: 'IO' },
  { name: 'Prairie Pioneers', ownerName: 'Grace Kim', logoEmoji: '🌾', acronym: 'PP' },
  { name: 'Coastal Cyclones', ownerName: 'Andre Dubois', logoEmoji: '🌀', acronym: 'CC' },
];

// 15 completed seasons (2011–2025). Champion/runner-up/3rd names line up with
// SAMPLE_TEAMS so the franchise trophy check (by team name or owner) lights up
// the right franchises across History, Standings and the roster header. The
// user's franchise (Turner Tomahawks / "You") has a spread of placements —
// runner-up in 2025, 2024, 2016 & 2017 and 3rd in 2023 & 2020. Double Down
// (Ian Duffy) is a three-time champ (2025, 2024, 2016) and 2014 runner-up.
const SAMPLE_HISTORY = [
  {
    id: 'season-2025', year: 2025,
    champion: 'Ian Duffy', championTeamName: 'Double Down',
    runnerUp: 'You', runnerUpTeamName: 'Turner Tomahawks',
    thirdPlace: 'Grace Kim', thirdPlaceTeamName: 'Prairie Pioneers',
    notes: 'Double Down edged the Tomahawks in the final category to take the title.',
  },
  {
    id: 'season-2024', year: 2024,
    champion: 'Ian Duffy', championTeamName: 'Double Down',
    runnerUp: 'You', runnerUpTeamName: 'Turner Tomahawks',
    thirdPlace: 'Kenji Watanabe', thirdPlaceTeamName: 'Summit Serpents',
    notes: 'Wire-to-wire championship season for Double Down.',
  },
  {
    id: 'season-2023', year: 2023,
    champion: 'Sofia Alvarez', championTeamName: 'Delta Dynamos',
    runnerUp: 'Dwayne Foster', runnerUpTeamName: 'Motor City Maulers',
    thirdPlace: 'You', thirdPlaceTeamName: 'Turner Tomahawks',
  },
  {
    id: 'season-2022', year: 2022,
    champion: 'Elena Petrova', championTeamName: 'Cascade Kings',
    runnerUp: 'Sofia Alvarez', runnerUpTeamName: 'Delta Dynamos',
    thirdPlace: "Liam O'Connor", thirdPlaceTeamName: 'Ironclad Owls',
  },
  {
    id: 'season-2021', year: 2021,
    champion: 'Kenji Watanabe', championTeamName: 'Summit Serpents',
    runnerUp: 'Priya Nair', runnerUpTeamName: 'Harbor Hounds',
    thirdPlace: 'Andre Dubois', thirdPlaceTeamName: 'Coastal Cyclones',
  },
  {
    id: 'season-2020', year: 2020,
    champion: 'Dwayne Foster', championTeamName: 'Motor City Maulers',
    runnerUp: 'Andre Dubois', runnerUpTeamName: 'Coastal Cyclones',
    thirdPlace: 'You', thirdPlaceTeamName: 'Turner Tomahawks',
    notes: 'The pandemic-shortened season crowned Motor City in a 6-team playoff.',
  },
  {
    id: 'season-2019', year: 2019,
    champion: 'Grace Kim', championTeamName: 'Prairie Pioneers',
    runnerUp: 'Elena Petrova', runnerUpTeamName: 'Cascade Kings',
    thirdPlace: 'Sofia Alvarez', thirdPlaceTeamName: 'Delta Dynamos',
  },
  {
    id: 'season-2018', year: 2018,
    champion: 'Priya Nair', championTeamName: 'Harbor Hounds',
    runnerUp: 'Elena Petrova', runnerUpTeamName: 'Cascade Kings',
    thirdPlace: 'Kenji Watanabe', thirdPlaceTeamName: 'Summit Serpents',
  },
  {
    id: 'season-2017', year: 2017,
    champion: "Liam O'Connor", championTeamName: 'Ironclad Owls',
    runnerUp: 'You', runnerUpTeamName: 'Turner Tomahawks',
    thirdPlace: 'Dwayne Foster', thirdPlaceTeamName: 'Motor City Maulers',
  },
  {
    id: 'season-2016', year: 2016,
    champion: 'Ian Duffy', championTeamName: 'Double Down',
    runnerUp: 'You', runnerUpTeamName: 'Turner Tomahawks',
    thirdPlace: 'Grace Kim', thirdPlaceTeamName: 'Prairie Pioneers',
    notes: 'First franchise title for Double Down.',
  },
  {
    id: 'season-2015', year: 2015,
    champion: 'Andre Dubois', championTeamName: 'Coastal Cyclones',
    runnerUp: "Liam O'Connor", runnerUpTeamName: 'Ironclad Owls',
    thirdPlace: 'Priya Nair', thirdPlaceTeamName: 'Harbor Hounds',
  },
  {
    id: 'season-2014', year: 2014,
    champion: 'Kenji Watanabe', championTeamName: 'Summit Serpents',
    runnerUp: 'Ian Duffy', runnerUpTeamName: 'Double Down',
    thirdPlace: 'Elena Petrova', thirdPlaceTeamName: 'Cascade Kings',
  },
  {
    id: 'season-2013', year: 2013,
    champion: 'Sofia Alvarez', championTeamName: 'Delta Dynamos',
    runnerUp: 'Dwayne Foster', runnerUpTeamName: 'Motor City Maulers',
    thirdPlace: 'Grace Kim', thirdPlaceTeamName: 'Prairie Pioneers',
  },
  {
    id: 'season-2012', year: 2012,
    champion: 'Kenji Watanabe', championTeamName: 'Summit Serpents',
    runnerUp: 'Andre Dubois', runnerUpTeamName: 'Coastal Cyclones',
    thirdPlace: "Liam O'Connor", thirdPlaceTeamName: 'Ironclad Owls',
  },
  {
    id: 'season-2011', year: 2011,
    champion: 'Elena Petrova', championTeamName: 'Cascade Kings',
    runnerUp: 'Priya Nair', runnerUpTeamName: 'Harbor Hounds',
    thirdPlace: 'Dwayne Foster', thirdPlaceTeamName: 'Motor City Maulers',
    notes: 'The inaugural season of the league.',
  },
];

function buildSampleTeams(userId, user) {
  const displayName =
    user?.fullName || user?.name ||
    `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
    user?.email?.split('@')[0] || 'You';
  return SAMPLE_TEAMS.map((t, i) => ({
    id: `team-${i + 1}`,
    teamId: `team-${i + 1}`,
    name: t.name,
    acronym: t.acronym,
    logoEmoji: t.logoEmoji,
    // Team 1 belongs to the signed-in user so userTeam resolves to it.
    owner: i === 0 ? (userId || 'sample-user') : `sample-owner-${i + 1}`,
    ownerName: i === 0 ? displayName : t.ownerName,
  }));
}

/**
 * Overlay a full sample league (teams + H2H scoring + history) so standings,
 * live matchup, scoreboard and history render without the API. Preserves the
 * league's identity (id / name / season) and any other settings it already had.
 */
export function enrichLeagueWithFixtures(league, userId, user) {
  if (!league) return league;
  const settings = (league.settings && typeof league.settings === 'object') ? league.settings : {};
  return {
    ...league,
    teams: buildSampleTeams(userId, user),
    settings: {
      ...settings,
      // Head-to-Head so the live matchup + W-L-T standings render.
      scoringType: settings.scoringType && String(settings.scoringType).includes('Head-to-Head')
        ? settings.scoringType
        : 'Head-to-Head Each Category',
      history: Array.isArray(settings.history) && settings.history.length > 0
        ? settings.history
        : SAMPLE_HISTORY,
    },
  };
}
